import { inject, Injectable, makeStateKey, OnDestroy, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';

import {
  combineLatest,
  distinctUntilKeyChanged,
  map,
  Observable,
  of,
  ReplaySubject,
  Subscription,
  take,
  tap,
} from 'rxjs';

import { CookieService } from '@fcom/core/services';
import { ConfigService, StateTransferService } from '@fcom/core';
import { entrySet, unsubscribe } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';

import { GtmService } from '../../gtm';
import {
  AKAMAI_AB_SEGMENT_COOKIE,
  akamaiSegmentToNumericMap,
  GtmEvent,
  GtmMultivariateEvent,
  MANUAL_AB_SEGMENT_COOKIE,
  MULTIVARIATE_TEST_COOKIES,
  MultivariateTestConfiguration,
  MultivariateTestCookies,
  MultivariateTestId,
  MultivariateTestOptions,
  Segment,
  TestVariant,
  TestVariantWithId,
  UserVariantSegment,
  VariantSource,
} from '../../interfaces';

const CONTROL_SEGMENT = 0 as Segment;

export const RUNNING_TESTS: MultivariateTestConfiguration[] = [
  // This is a workaround to hide things from playwright regression tests in master
  {
    id: MultivariateTestId.REGRESSION_TEST_WORKAROUND,
    requiresInjectScript: false,
    segments: {
      [TestVariant.A]: [CONTROL_SEGMENT],
      [TestVariant.B]: [1, 2, 3, 4],
    },
    enabledInAllEnvs: false,
  },
  {
    id: MultivariateTestId.SHOPPING_CART_TWO,
    requiresInjectScript: false,
    segments: {
      [TestVariant.A]: [CONTROL_SEGMENT],
      [TestVariant.B]: [4],
    },
    enabledInAllEnvs: true,
  },
];

const RESTRICTED_BUILD_ENVS = ['preprod', 'staging', 'prod'];

export const generateSegmentResolver =
  (testId: MultivariateTestId, countryCode: string, runningTests: MultivariateTestConfiguration[], buildEnv: string) =>
  (segment: Segment): TestVariant => {
    const test = runningTests.find((t) => t.id === testId);

    if (!test) {
      // If no test setup we always resolve to NOT_IN_TEST
      return TestVariant.NOT_IN_TEST;
    }

    if (!test.enabledInAllEnvs && RESTRICTED_BUILD_ENVS.includes(buildEnv)) {
      return TestVariant.NOT_IN_TEST;
    }

    if (test.includeCountries?.length && !test.includeCountries.includes(countryCode)) {
      return TestVariant.NOT_IN_TEST;
    }

    const variant = entrySet(test.segments).find((entry) => entry.value.includes(segment));

    if (!variant) {
      return TestVariant.NOT_IN_TEST;
    }

    const sameSegmentUsedForCountryTest = runningTests.some((testCase) => {
      if (!testCase?.includeCountries?.length) {
        return false;
      }

      if (testCase.id === testId) {
        return false;
      }

      const testEntries = entrySet(testCase.segments);

      return (
        testCase.includeCountries?.includes(countryCode) &&
        testEntries
          .filter((entry) => entry.key !== TestVariant.A)
          .some((entry) => entry.value.some((segment) => variant.value.includes(segment)))
      );
    });

    if (sameSegmentUsedForCountryTest && testId !== MultivariateTestId.REGRESSION_TEST_WORKAROUND) {
      return TestVariant.NOT_IN_TEST;
    }

    return variant.key as TestVariant;
  };

@Injectable()
export class MultivariateTestService implements OnDestroy {
  private cookieService = inject(CookieService);
  private gtmService = inject(GtmService);
  private stateTransferService = inject(StateTransferService);
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private platform = inject(PLATFORM_ID);
  private document = inject<Document>(DOCUMENT);
  private readonly multivariateTestCookies = inject<MultivariateTestCookies>(MULTIVARIATE_TEST_COOKIES, {
    optional: true,
  });

  segmentsFeed$: ReplaySubject<UserVariantSegment> = new ReplaySubject(1);

  reportedTestIds: MultivariateTestId[] = [];

  private subscriptions = new Subscription();

  constructor() {
    this.stateTransferService
      .wrapToStateCache(makeStateKey<UserVariantSegment>('ab-test-segment'), () => of(this.getSegmentFromCookies()))
      .pipe(take(1))
      .subscribe((value) => {
        const nextValue = value || { segment: 0, source: VariantSource.RANDOMIZED };
        this.segmentsFeed$.next(nextValue);

        this.injectAkamaiSegmentToBodyIfRequired(nextValue);
      });
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  /**
   * This is returning a subscription to segment, you need to provide your experiment
   * id and remember to unsubscribe.
   * It reports your id and the dynamic segment to gtmService (segment can change from initial after CAS response).
   * @param testId
   * @param variant
   */
  reportTest(testId: string, variant: string, options?: MultivariateTestOptions): void {
    if (isPlatformServer(this.platform)) {
      return;
    }

    this.subscriptions.add(
      this.segmentsFeed$
        .pipe(distinctUntilKeyChanged('source'), distinctUntilKeyChanged('segment'))
        .subscribe((segment) => {
          const eventData: GtmMultivariateEvent = {
            testId,
            segment: segment.segment,
            source: segment.source,
            variant,
            ...(options?.label != null && { label: options.label }),
          };

          this.gtmService.pushEventToDataLayer(GtmEvent.MULTIVARIATE_TEST, eventData);
        })
    );
  }

  getTestVariant(
    id: MultivariateTestId,
    autoReportTest = true,
    skipCountryCheck = false,
    options?: MultivariateTestOptions
  ): Observable<TestVariantWithId> {
    const variant$ = combineLatest([
      this.segmentsFeed$,
      skipCountryCheck ? of('int') : this.languageService.countryCode,
    ]).pipe(
      map(([userVariantSegment, countryCode]) => {
        const segmentResolver = generateSegmentResolver(
          id,
          countryCode,
          RUNNING_TESTS,
          this.configService.cfg.buildEnv
        );
        const variant = segmentResolver(userVariantSegment.segment);
        return { variant, id };
      })
    );

    if (autoReportTest && !isPlatformServer(this.platform)) {
      return variant$.pipe(
        tap(({ id, variant }) => {
          if (variant !== TestVariant.NOT_IN_TEST && !this.reportedTestIds.includes(id)) {
            this.reportTest(id, variant, options);
            this.reportedTestIds.push(id);
          }
        }),
        finShare()
      );
    }

    return variant$;
  }

  // This is here because service is injected to app component, and needs a method call not to crash linters
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  init(): void {}

  private getCookieValue(name: string): string | null {
    if (isPlatformServer(this.platform)) {
      return this.multivariateTestCookies?.[name] ?? '';
    }
    return this.cookieService.getItem(name);
  }

  private getSegmentFromCookies(): UserVariantSegment {
    const manualSegment = this.getCookieValue(MANUAL_AB_SEGMENT_COOKIE);

    if (manualSegment) {
      return {
        segment: Number(manualSegment) as Segment,
        source: VariantSource.MANUAL,
      };
    }

    return {
      segment: akamaiSegmentToNumericMap[this.getCookieValue(AKAMAI_AB_SEGMENT_COOKIE)] || 0,
      source: VariantSource.AKAMAI,
    };
  }

  private injectAkamaiSegmentToBodyIfRequired(variantSegment: UserVariantSegment): void {
    if (RUNNING_TESTS.some((test) => test.requiresInjectScript)) {
      const script = this.document.createElement('script');
      script.innerHTML = `(function (d) {
          d.body.setAttribute('data-segment', ${variantSegment.segment});
        })(document);`;
      this.document.body.appendChild(script);
    }
  }
}
